import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { AlertTriangle, Coffee, LogIn, LogOut, MapPin, Play, RefreshCw, Satellite } from 'lucide-react';
import { EVENT_TYPE_LABEL, type ClockAction } from '@wulftime/core';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { fmtDateLong, fmtMinutes, fmtTime } from '../../lib/format';
import { getPosition, type GpsPayload } from '../../lib/geo';
import { enqueue, markFailed, newClientEventId, pending, remove } from '../../lib/offline';
import { Alert, Button, Card, Field, Modal, Spinner, Textarea, useToast, cx } from '../../components/ui';
import { DayTypeBadge, RecordStatusBadge } from '../../components/status';

interface Today {
  employee: { id: string; firstName: string };
  timezone: string;
  serverTime: string;
  record: { id: string; workDate: string; status: 'NOT_STARTED' | 'AUTO_STARTED' | 'WORKING' | 'ON_BREAK' | 'FINISHED'; statusLabel: string; dayType: string; computed: any; autoStartedAt: string | null };
  planned: any;
  events: { id: string; type: keyof typeof EVENT_TYPE_LABEL; occurredAt: string; source: string; personal: boolean; timestampBasis: string; breakId: string | null }[];
  allowedActions: ClockAction[];
  policy: { methods: Record<string, boolean>; earlyWindowMinutes: number; gps: { clockIn: string; clockOut: string; breaks: string; maxAccuracyMeters: number }; offlineAllowed: boolean };
  centers: { id: string; name: string; radiusMeters: number }[];
  gpsNotice: { required: boolean; text: string | null; acknowledgedAt: string | null };
  pendingPresenceVerifications: number;
}

interface ClockResponse {
  status: 'RECORDED' | 'REJECTED' | 'NEEDS_REASON' | 'DUPLICATE' | 'SKIPPED';
  decision: null | { errors: { code: string; message: string; hint?: string }[]; reasonRequiredFor: { code: string; message: string }[]; warnings: { message: string }[]; anomalies: { code: string; message: string }[]; gps: { result: string; distanceMeters: number | null; centerName: string | null } };
  incidents: string[];
}

const ACTION_LABEL: Record<ClockAction, string> = { CLOCK_IN: 'Fichar entrada', BREAK_START: 'Iniciar pausa', BREAK_END: 'Reanudar', CLOCK_OUT: 'Fichar salida' };
const ACTION_DONE: Record<ClockAction, string> = { CLOCK_IN: 'Entrada registrada', BREAK_START: 'Pausa iniciada', BREAK_END: 'Jornada reanudada', CLOCK_OUT: 'Salida registrada' };

function useNow(offsetMs: number) {
  const [now, setNow] = useState(() => Date.now() + offsetMs);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() + offsetMs), 1000);
    return () => clearInterval(t);
  }, [offsetMs]);
  return now;
}

const gpsReqFor = (t: Today, a: ClockAction) => (a === 'CLOCK_IN' ? t.policy.gps.clockIn : a === 'CLOCK_OUT' ? t.policy.gps.clockOut : t.policy.gps.breaks);

export function WorkerHome() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: t, isLoading, error, refetch } = useQuery({ queryKey: ['me', 'today'], queryFn: () => api.get<Today>('/api/v1/me/today'), refetchInterval: 60_000 });
  const [offset, setOffset] = useState(0);
  const now = useNow(offset);
  const [phase, setPhase] = useState<'idle' | 'locating' | 'sending'>('idle');
  const [reasonModal, setReasonModal] = useState<{ action: ClockAction; gps: GpsPayload | null; reasons: { message: string }[] } | null>(null);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<{ action: ClockAction; errors: { code: string; message: string; hint?: string }[] } | null>(null);
  const [noticeFor, setNoticeFor] = useState<ClockAction | null>(null);
  const [queued, setQueued] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const scope = `worker:${me?.company?.employee?.id ?? 'x'}`;

  useEffect(() => {
    if (t) setOffset(new Date(t.serverTime).getTime() - Date.now());
  }, [t?.serverTime]);

  const sync = useCallback(async () => {
    const items = await pending(scope);
    setQueued(items.length);
    if (!items.length || !navigator.onLine) return;
    try {
      const r = await api.post<{ results: { clientEventId: string; status: string; needsReview: boolean; reasons: string[] }[] }>('/api/v1/me/sync', {
        deviceClockAtSync: new Date().toISOString(),
        method: 'WEB',
        events: items.map((i) => ({ clientEventId: i.clientEventId, action: i.action, deviceRecordedAt: i.deviceRecordedAt, gps: i.gps ?? null, reason: i.reason ?? null })),
      });
      for (const res of r.results) {
        const item = items.find((i) => i.clientEventId === res.clientEventId)!;
        if (['RECORDED', 'DUPLICATE', 'SKIPPED'].includes(res.status)) await remove(res.clientEventId);
        else await markFailed(item, res.reasons.join(' '));
      }
      const recorded = r.results.filter((x) => x.status === 'RECORDED').length;
      if (recorded) toast.success(`${recorded} fichaje(s) sin conexión sincronizados${r.results.some((x) => x.needsReview) ? ' (pendientes de revisión)' : ''}`);
      setQueued((await pending(scope)).length);
      qc.invalidateQueries({ queryKey: ['me'] });
    } catch {
      /* se reintenta más tarde */
    }
  }, [scope, qc, toast]);

  useEffect(() => {
    sync();
    const on = () => {
      setOnline(true);
      sync();
    };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [sync]);

  const send = useMutation({
    mutationFn: async (v: { action: ClockAction; gps: GpsPayload | null; reason?: string }) => {
      const clientEventId = newClientEventId();
      try {
        return { res: await api.post<ClockResponse>('/api/v1/me/clock', { action: v.action, gps: v.gps, reason: v.reason ?? null, clientEventId, method: 'WEB' }), v };
      } catch (e) {
        if (e instanceof ApiError && e.status === 422 && e.body) return { res: e.body as ClockResponse, v };
        if (e instanceof ApiError && e.status === 0 && t?.policy.offlineAllowed) {
          await enqueue({ clientEventId, scope, action: v.action, deviceRecordedAt: new Date().toISOString(), gps: v.gps, reason: v.reason ?? null });
          setQueued((q) => q + 1);
          return { res: { status: 'QUEUED' } as unknown as ClockResponse, v };
        }
        throw e;
      }
    },
    onSuccess: ({ res, v }) => {
      setPhase('idle');
      if ((res.status as string) === 'QUEUED') {
        toast.info('Sin conexión: el fichaje se ha guardado en este dispositivo con su hora y se enviará al recuperar la conexión.');
        return;
      }
      if (res.status === 'RECORDED' || res.status === 'DUPLICATE') {
        setFailure(null);
        setReasonModal(null);
        setReason('');
        toast.success(res.status === 'DUPLICATE' ? 'Este fichaje ya estaba registrado.' : ACTION_DONE[v.action]);
        for (const w of res.decision?.warnings ?? []) toast.info(w.message);
        qc.invalidateQueries({ queryKey: ['me'] });
      } else if (res.status === 'NEEDS_REASON') {
        setReasonModal({ action: v.action, gps: v.gps, reasons: res.decision?.reasonRequiredFor ?? [] });
      } else {
        setFailure({ action: v.action, errors: res.decision?.errors ?? [{ code: 'ERROR', message: 'No se ha podido registrar el fichaje.' }] });
      }
    },
    onError: (e) => {
      setPhase('idle');
      if (e instanceof ApiError && e.code === 'STALE_OPEN_DAY') setFailure({ action: 'CLOCK_IN', errors: [{ code: e.code, message: e.message }] });
      else toast.error(e);
    },
  });

  const doClock = async (action: ClockAction, opts: { skipNotice?: boolean; reasonText?: string; gps?: GpsPayload | null } = {}) => {
    if (!t) return;
    const req = gpsReqFor(t, action);
    if (req !== 'DISABLED' && !t.gpsNotice.acknowledgedAt && !opts.skipNotice) {
      setNoticeFor(action);
      return;
    }
    let gps: GpsPayload | null = opts.gps ?? null;
    if (req !== 'DISABLED' && opts.gps === undefined) {
      setPhase('locating');
      gps = await getPosition();
    }
    setPhase('sending');
    send.mutate({ action, gps, reason: opts.reasonText });
  };

  const respondVerification = async () => {
    const list = await api.get<{ id: string; status: string; reason: string; expiresAt: string }[]>('/api/v1/me/presence-verifications');
    const p = list.find((x) => x.status === 'PENDING');
    if (!p) return refetch();
    const gps = await getPosition();
    try {
      const r = await api.post(`/api/v1/me/presence-verifications/${p.id}/respond`, { gps });
      toast.success(`Verificación enviada: ${r.gps?.result === 'VALID' ? 'ubicación válida' : 'ubicación registrada'}`);
    } catch (e) {
      toast.error(e);
    }
    refetch();
  };

  useEffect(() => {
    if (params.get('verify') === '1' && t?.pendingPresenceVerifications) void respondVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, t?.pendingPresenceVerifications]);

  const computed = t?.record.computed;
  const derived = useMemo(() => {
    if (!t) return null;
    const ev = t.events;
    const lastIn = [...ev].reverse().find((e) => e.type === 'CLOCK_IN');
    const lastBreak = [...ev].reverse().find((e) => e.type === 'BREAK_START');
    const firstSeg = t.planned.segments?.[0];
    const lastSeg = t.planned.segments?.[t.planned.segments.length - 1];
    const nextBreak = (t.planned.breaks ?? []).find((b: any) => b.startAt && !ev.some((e) => e.type === 'BREAK_START' && e.breakId === b.id));
    const breakDef = lastBreak ? (t.planned.breaks ?? []).find((b: any) => b.id === lastBreak.breakId) : null;
    const earliest = firstSeg ? DateTime.fromISO(firstSeg.start).minus({ minutes: t.policy.earlyWindowMinutes }).toISO() : null;
    return { lastIn, lastBreak, firstSeg, lastSeg, nextBreak, breakDef, earliest };
  }, [t]);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="error">{(error as Error).message}</Alert>;
  if (!t || !derived) return null;

  const status = t.record.status;
  const busy = phase !== 'idle';
  const elapsedSince = (iso: string) => Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  // Cómputo provisional del servidor + tiempo transcurrido desde la consulta (contador en vivo).
  const workedNow = (computed?.real.effectiveMinutes ?? 0) + (status === 'WORKING' ? elapsedSince(t.serverTime) : 0);
  const anyGps = (['CLOCK_IN', 'CLOCK_OUT'] as ClockAction[]).some((a) => gpsReqFor(t, a) !== 'DISABLED');

  const primary: ClockAction | null = status === 'ON_BREAK' ? 'BREAK_END' : status === 'WORKING' ? 'BREAK_START' : t.allowedActions.includes('CLOCK_IN') ? 'CLOCK_IN' : null;
  // La salida solo aparece con la entrada fichada (tras un inicio automático, primero hay que fichar la entrada)
  const secondary: ClockAction | null = status === 'WORKING' && t.allowedActions.includes('CLOCK_OUT') ? 'CLOCK_OUT' : null;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-sm text-slate-500">{fmtDateLong(DateTime.fromMillis(now).setZone(t.timezone).toISODate())}</p>
          <h1 className="text-xl font-semibold">Hola, {t.employee.firstName}</h1>
        </div>
        <p className="text-3xl font-semibold tabular-nums text-slate-900" aria-label="Hora actual">
          {DateTime.fromMillis(now).setZone(t.timezone).toFormat('HH:mm:ss')}
        </p>
      </div>

      {(!online || queued > 0) && (
        <Alert tone="warning" title={!online ? 'Sin conexión' : 'Fichajes pendientes de enviar'}>
          {queued > 0 ? `${queued} fichaje(s) guardado(s) en este dispositivo con su hora. Se enviarán automáticamente al recuperar la conexión y quedarán marcados como fichajes sin conexión.` : 'Si fichas ahora, se guardará en este dispositivo y se enviará después.'}
          {online && queued > 0 && (
            <Button size="sm" variant="secondary" className="mt-2" onClick={sync} icon={<RefreshCw className="h-3.5 w-3.5" />}>
              Sincronizar ahora
            </Button>
          )}
        </Alert>
      )}

      {t.pendingPresenceVerifications > 0 && (
        <Alert tone="info" title="WulfTime solicita comprobar tu ubicación">
          Tu empresa ha solicitado una verificación puntual de presencia. Solo se enviará tu ubicación actual una vez.
          <Button size="sm" className="mt-2" onClick={respondVerification} icon={<MapPin className="h-3.5 w-3.5" />}>
            Compartir ubicación ahora
          </Button>
        </Alert>
      )}

      <Card padded={false} className="overflow-hidden">
        <div className={cx('px-5 py-4', status === 'WORKING' ? 'bg-emerald-50' : status === 'ON_BREAK' ? 'bg-amber-50' : status === 'FINISHED' ? 'bg-brand-50' : 'bg-slate-50')}>
          <div className="flex items-center justify-between gap-2">
            <RecordStatusBadge status={status} />
            <DayTypeBadge dayType={t.record.dayType} label={t.planned.label} />
          </div>
          {status === 'WORKING' && (
            <p className="mt-3 text-4xl font-semibold tabular-nums text-emerald-800" aria-live="polite">
              {fmtMinutes(workedNow)}
            </p>
          )}
          {status === 'ON_BREAK' && derived.lastBreak && <p className="mt-3 text-4xl font-semibold tabular-nums text-amber-800">{fmtMinutes(elapsedSince(derived.lastBreak.occurredAt))}</p>}
          {status === 'FINISHED' && <p className="mt-3 text-4xl font-semibold tabular-nums text-brand-900">{fmtMinutes(computed?.computable.effectiveMinutes ?? 0)}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4 px-5 py-4 text-sm sm:grid-cols-3">
          {status === 'NOT_STARTED' || status === 'AUTO_STARTED' ? (
            <>
              <Info label="Jornada prevista" value={t.planned.segments?.length ? t.planned.segments.map((s: any) => `${s.startLocal}–${s.endLocal}`).join(' / ') : t.planned.dayType === 'WORKDAY' ? 'Flexible' : 'Sin jornada'} />
              <Info label="Puedes fichar desde" value={derived.earliest ? fmtTime(derived.earliest, t.timezone) : '—'} />
              <Info label="Centro" value={t.centers.map((c) => c.name).join(', ') || 'Sin centro fijo'} />
              <Info label="Método" value={anyGps ? 'Con ubicación (solo al fichar)' : 'Sin ubicación'} />
              {status === 'AUTO_STARTED' && (
                <>
                  <Info label="Inicio previsto/automático" value={fmtTime(t.record.autoStartedAt, t.timezone)} />
                  <Info label="Fichaje personal" value={<span className="font-semibold text-amber-700">Pendiente</span>} />
                </>
              )}
            </>
          ) : status === 'WORKING' ? (
            <>
              <Info label="Entrada real" value={fmtTime(derived.lastIn?.occurredAt, t.timezone)} />
              <Info label="Próxima pausa" value={derived.nextBreak ? `${derived.nextBreak.name} · ${fmtTime(derived.nextBreak.startAt, t.timezone)}` : '—'} />
              <Info label="Salida prevista" value={derived.lastSeg?.endLocal ?? '—'} />
            </>
          ) : status === 'ON_BREAK' ? (
            <>
              <Info label="Inicio de pausa" value={fmtTime(derived.lastBreak?.occurredAt, t.timezone)} />
              <Info label="Fin previsto" value={derived.lastBreak && derived.breakDef ? fmtTime(DateTime.fromISO(derived.lastBreak.occurredAt).plus({ minutes: derived.breakDef.durationMinutes }).toISO(), t.timezone) : '—'} />
              <Info label="Pausa" value={derived.breakDef?.name ?? 'No planificada'} />
            </>
          ) : (
            <>
              <Info label="Entrada" value={fmtTime(computed?.real.firstIn, t.timezone)} />
              <Info label="Salida" value={fmtTime(computed?.real.lastOut, t.timezone)} />
              <Info label="Pausas" value={fmtMinutes(computed?.real.breakMinutes)} />
              <Info label="Horas efectivas" value={fmtMinutes(computed?.computable.effectiveMinutes)} />
              <Info label="Previstas" value={fmtMinutes(computed?.planned.minutes)} />
              <Info label="Diferencia" value={fmtMinutes(computed?.differences.vsPlannedMinutes, { sign: true })} />
            </>
          )}
        </div>

        {(primary || secondary) && (
          <div className="flex flex-col gap-2 border-t border-slate-100 p-4 sm:flex-row">
            {primary && (
              <Button
                size="lg"
                className="h-16 flex-1 text-lg"
                variant={primary === 'CLOCK_IN' ? 'primary' : primary === 'BREAK_START' ? 'secondary' : 'success'}
                onClick={() => doClock(primary)}
                loading={busy && send.variables?.action !== secondary}
                icon={primary === 'CLOCK_IN' ? <LogIn className="h-5 w-5" /> : primary === 'BREAK_START' ? <Coffee className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              >
                {phase === 'locating' ? 'Obteniendo ubicación…' : ACTION_LABEL[primary].toUpperCase()}
              </Button>
            )}
            {secondary && (
              <Button size="lg" variant="danger" className="h-16 flex-1 text-lg" onClick={() => doClock(secondary)} disabled={busy} icon={<LogOut className="h-5 w-5" />}>
                {ACTION_LABEL[secondary].toUpperCase()}
              </Button>
            )}
          </div>
        )}
        {anyGps && status !== 'FINISHED' && (
          <p className="flex items-center gap-1.5 border-t border-slate-100 px-5 py-2.5 text-xs text-slate-500">
            <Satellite className="h-3.5 w-3.5" /> Tu ubicación se consulta solo en el momento de fichar. WulfTime no realiza seguimiento.
          </p>
        )}
      </Card>

      {failure && (
        <Alert tone="error" title={`No se ha podido ${ACTION_LABEL[failure.action].toLowerCase()}`}>
          {failure.errors.map((e) => (
            <div key={e.code} className="mt-1">
              <p>{e.message}</p>
              {e.hint && <p className="mt-1 text-xs opacity-90">{e.hint}</p>}
            </div>
          ))}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => doClock(failure.action)} icon={<RefreshCw className="h-3.5 w-3.5" />}>
              Reintentar
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate(`/app/requests?new=incident&type=${failure.errors.some((e) => e.code.startsWith('GPS')) ? 'GPS' : 'COULD_NOT_CLOCK'}`)} icon={<AlertTriangle className="h-3.5 w-3.5" />}>
              Abrir incidencia
            </Button>
            {failure.errors[0]?.code === 'STALE_OPEN_DAY' && (
              <Button size="sm" onClick={() => navigate('/app/history')}>
                Solicitar corrección
              </Button>
            )}
          </div>
        </Alert>
      )}

      {t.events.length > 0 && (
        <Card title="Fichajes de hoy">
          <ol className="relative space-y-3 border-l-2 border-slate-100 pl-5">
            {t.events.map((e) => (
              <li key={e.id} className="relative">
                <span className={cx('absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-white', e.type === 'CLOCK_IN' ? 'bg-emerald-500' : e.type === 'CLOCK_OUT' ? 'bg-red-500' : e.type === 'AUTO_START' ? 'bg-violet-400' : 'bg-amber-400')} />
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-medium">{EVENT_TYPE_LABEL[e.type]}</span>
                  <span className="tabular-nums text-slate-600">{fmtTime(e.occurredAt, t.timezone)}</span>
                </div>
                <p className="text-xs text-slate-500">
                  {e.personal ? 'Fichaje personal' : 'Generado por el sistema / corrección'} · {e.source === 'KIOSK' ? 'Kiosco' : e.source === 'MOBILE' ? 'App' : e.source === 'WEB' ? 'Web' : e.source}
                  {e.timestampBasis === 'DEVICE' && ' · hora del dispositivo (sin conexión)'}
                </p>
              </li>
            ))}
          </ol>
        </Card>
      )}

      <Modal
        open={Boolean(reasonModal)}
        onClose={() => setReasonModal(null)}
        title="Indica el motivo"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReasonModal(null)}>
              Cancelar
            </Button>
            <Button disabled={reason.trim().length < 3} loading={send.isPending} onClick={() => reasonModal && doClock(reasonModal.action, { reasonText: reason, gps: reasonModal.gps, skipNotice: true })}>
              Fichar con motivo
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {reasonModal?.reasons.map((r, i) => (
            <Alert key={i} tone="warning">
              {r.message}
            </Alert>
          ))}
          <Field label="Motivo">{(id) => <Textarea id={id} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explica brevemente el motivo" />}</Field>
          <p className="text-xs text-slate-500">El fichaje se registrará con la hora real y el motivo quedará asociado al evento.</p>
        </div>
      </Modal>

      <Modal
        open={Boolean(noticeFor)}
        onClose={() => setNoticeFor(null)}
        title="Uso de tu ubicación al fichar"
        footer={
          <Button
            onClick={async () => {
              await api.post('/api/v1/me/gps-notice/ack').catch(() => {});
              const a = noticeFor!;
              setNoticeFor(null);
              await qc.invalidateQueries({ queryKey: ['me', 'today'] });
              doClock(a, { skipNotice: true });
            }}
          >
            Entendido, continuar
          </Button>
        }
      >
        <div className="space-y-3 text-sm text-slate-700">
          <p>Tu empresa utiliza la ubicación para verificar que el fichaje se realiza en el centro de trabajo.</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Se consulta solo en el momento de fichar, nunca de forma continua.</li>
            <li>Se guarda junto al fichaje: posición, precisión y distancia al centro.</li>
            <li>Tu navegador o tu móvil te pedirán permiso.</li>
          </ul>
          {t.gpsNotice.text && <p className="whitespace-pre-line rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{t.gpsNotice.text}</p>}
        </div>
      </Modal>

    </div>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-medium text-slate-900">{value}</p>
    </div>
  );
}
